import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { Backdrop, CircularProgress, useTheme } from "@mui/material";
import axios from "axios";

import backend from "../../../../api";
import { setUserData } from "../../../../redux-store/auth/actions";

import Classes from "./Profile.module.css";

import BasicInfo from "./Comp/BasicInfo";
import Experience from "./Comp/Experience";
import SessionDetails from "./Comp/SessionDetails";
import BookingSetup from "./Comp/BookingSetup";

import {
  ProfileIcon,
  BriefCaseIcon,
  CreditCardIcon,
} from "../../../../utils/Icons";

const slide = {
  initial: { x: 10, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -10, opacity: 0 },
};

export default function Mentor() {
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const theme = useTheme();

  const [activePage, setActive] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  /* ─────────── upload helper (same endpoint you provided) ─────────── */
  const uploadMedia = async (files) => {
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));

      const res = await axios.post(
        "https://mentr-aws-server-668d71936193.herokuapp.com/api/deck/upload",
        fd
      );
      return res.data.data[0]?.imgurl;
    } finally {
      setUploading(false);
    }
  };

  console.log(user)

  /* ─────────── PUT /user/:id helper ───────────
     markDone=true → sets onboarded:true on the server               */
  const saveProfile = useCallback(
    async (patch, markDone = false) => {
      setSaving(true);
      try {
        const payload = {
          mentorProfile: { ...user.mentorProfile, ...patch },
          onboarded: markDone ? true : user?.onboarded,
        };
        const { data } = await backend.put(`/api/user/${user._id}`, payload);
        console.log('Updated user', data.data)
        await dispatch(setUserData({ ...user, ...data.data }));
        if (markDone) {
          window.location.replace("/");
        }
      } finally {
        setSaving(false);
      }
    },
    [dispatch, user]
  );

  /* ─────────── sidebar steps ─────────── */
  const steps = [
    { id: 1, label: "Basic Info", icon: <ProfileIcon height="15" fill="white" /> },
    { id: 2, label: "Experience", icon: <BriefCaseIcon height="17" fill="white" /> },
    { id: 3, label: "Session Details", icon: <img src="/assets/icons/session-details.png" height={17} width={17} /> },
    { id: 4, label: "Payments", icon: <CreditCardIcon height="17" fill="white" /> },
  ];

  /* ─────────── dynamic step renderer ─────────── */
  const StepComponent = (() => {
    switch (activePage) {
      case 1:
        return (
          <BasicInfo
            initial={user?.mentorProfile?.basicInfo}
            uploadMedia={uploadMedia}
            saveProfile={saveProfile}
            onNext={() => setActive(2)}
          />
        );
      case 2:
        return (
          <Experience
            initial={user?.mentorProfile?.experience}
            uploadMedia={uploadMedia}
            saveProfile={saveProfile}
            onNext={() => setActive(3)}
            onBack={() => setActive(1)}
          />
        );
      case 3:
        return (
          <SessionDetails
            initial={user?.mentorProfile?.sessionDetails}
            saveProfile={saveProfile}
            onNext={() => setActive(4)}
            onBack={() => setActive(2)}
          />
        );
      case 4:
        return (
          <BookingSetup
            initial={user?.mentorProfile?.bookingSetup}
            /*  saveProfile call marks onboarding complete  */
            saveProfile={(patch) => saveProfile(patch, true)}
            onBack={() => setActive(3)}
          />
        );
      default:
        return null;
    }
  })();

  /* ─────────── JSX ─────────── */
  return (
    <>
      {/* global spinner for saves & uploads */}
      <Backdrop
        open={saving || uploading}
        sx={{ zIndex: theme.zIndex.tooltip + 1, color: "#fff" }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className={Classes.hero}>
        <div className={Classes.header}>
          <h5>Mentr</h5>
        </div>

        <div className={Classes.body}>
          <div className={Classes.bodyInner}>
            <motion.h3
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              Build Your Profile
            </motion.h3>

            <motion.h5
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.05 }}
            >
              Don’t worry — you can edit this later.
            </motion.h5>

            {/* sidebar */}
            <motion.div
              className={Classes.stepper}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {steps.map((s, idx) => (
                <React.Fragment key={s.id}>
                  <div
                    role="button"
                    // onClick={() => setActive(s.id)}
                    className={`${Classes.sideElement} ${activePage === s.id ? Classes.current : ""
                      }`}
                  >
                    <div className={Classes.flexAlign10}>
                      <div
                        className={`${Classes.imageCon} ${activePage >= s.id ? Classes.active : ""
                          }`}
                      >
                        {s.icon}
                      </div>
                      <h5
                        className={
                          activePage >= s.id ? Classes.activeText : undefined
                        }
                      >
                        {s.label}
                      </h5>
                    </div>
                  </div>
                  {idx !== steps.length - 1 && (
                    <div
                      className={`${Classes.hr} ${activePage > s.id ? Classes.coloredHr : ""
                        }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </motion.div>

            {/* step content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activePage}
                variants={slide}
                initial="initial"
                animate="animate"
                exit="exit"
                className={Classes.infoMain}
              >
                {StepComponent}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
}
